import {
  Edit,
  Key,
  KeyRound,
  Mail,
  Package,
  Scan,
  Server,
  Shield,
  ShieldCheck,
  X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { isCenterLike } from "@/features/servers/utils/ficha";
import type { UserAction } from "@/features/users/components/UserActionsDialog";
import { UserInstallationsDialog } from "./UserInstallationsDialog";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

interface UserActionsBottomSheetProps {
  user: User;
  currentUser?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (user: User) => void;
  onAction: (user: User, action: UserAction) => void;
  onViewImagings?: (user: User) => void;
}

/**
 * Mobile-specific Bottom Sheet for User Actions.
 * Provides a native-like experience and avoids layout shifts.
 */
export function UserActionsBottomSheet({
  user,
  currentUser,
  isOpen,
  onClose,
  onEdit,
  onAction,
  onViewImagings,
}: UserActionsBottomSheetProps) {
  const { can } = usePermissions();
  const isCurrentUser = user.uid === currentUser?.uid;

  const canEdit = can("users:edit");
  const canManageAccount = can("users:toggle_active");
  const canAccessImaging = can("imagings:list");
  const canManageApiKeys = can("api_keys:list");
  const isDeployAdmin = can("installations:download_package");

  // Installations dialog state (instituciones y médicos)
  const [showInstallations, setShowInstallations] = useState(false);
  const canDownloadInstallations = isCenterLike(user) && isDeployAdmin;
  const canViewServers = can("servers:view") && isCenterLike(user);
  const navigate = useNavigate();

  if (showInstallations && canDownloadInstallations) {
    return (
      <UserInstallationsDialog
        user={user}
        open={showInstallations}
        onClose={() => setShowInstallations(false)}
      />
    );
  }

  if (!isOpen) return null;

  const ActionItem = ({
    icon,
    label,
    onClick,
    variant = "default",
    disabled = false,
  }: {
    icon: ReactNode;
    label: string;
    onClick: () => void;
    variant?: "default" | "destructive" | "teal" | "amber" | "blue" | "purple" | "green";
    disabled?: boolean;
  }) => {
    const getIconColor = () => {
      switch (variant) {
        case "destructive":
          return "text-red-500";
        case "teal":
          return "text-teal-600";
        case "amber":
          return "text-amber-500";
        case "blue":
          return "text-blue-500";
        case "purple":
          return "text-purple-500";
        case "green":
          return "text-green-500";
        default:
          return "text-primary";
      }
    };

    return (
      <button
        onClick={() => {
          onClick();
          onClose();
        }}
        disabled={disabled}
        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors active:bg-gray-100 dark:active:bg-gray-700/50 disabled:opacity-40 disabled:pointer-events-none`}
      >
        <div className={`${getIconColor()} shrink-0`}>{icon}</div>
        <span className="text-gray-700 dark:text-gray-200 font-medium">{label}</span>
      </button>
    );
  };

  const SectionLabel = ({ children }: { children: ReactNode }) => (
    <div className="px-5 py-1.5 mt-1 first:mt-0">
      <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        {children}
      </p>
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up safe-bottom flex justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-strong border-t border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[85vh] overflow-y-auto custom-scrollbar">
          {/* Handle bar */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 dark:border-gray-700/50">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                {getUserFullName(user)}
              </h3>
              <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Actions List */}
          <div className="py-1">
            <SectionLabel>Gestión</SectionLabel>
            {canEdit && (
              <ActionItem
                icon={<Edit className="h-4.5 w-4.5" />}
                label="Editar perfil / email"
                onClick={() => onEdit(user)}
                disabled={isCurrentUser}
              />
            )}
            {onViewImagings && canAccessImaging && (
              <ActionItem
                icon={<Scan className="h-4.5 w-4.5" />}
                label="Ver Imagenología"
                variant="teal"
                onClick={() => onViewImagings(user)}
              />
            )}
            {canDownloadInstallations && (
              <ActionItem
                icon={<Package className="h-4.5 w-4.5" />}
                label="Descargar Instalaciones"
                variant="blue"
                onClick={() => setShowInstallations(true)}
              />
            )}
            {canViewServers && (
              <ActionItem
                icon={<Server className="h-4.5 w-4.5" />}
                label="Infraestructura / Servidores"
                variant="blue"
                onClick={() => navigate(`/servers/${user.uid}`)}
              />
            )}

            {canManageAccount && (
              <>
                <div className="mx-5 my-1 border-t border-gray-100 dark:border-gray-700/50" />
                <SectionLabel>Contraseña</SectionLabel>
                <ActionItem
                  icon={<Mail className="h-4.5 w-4.5" />}
                  label="Enviar enlace por correo"
                  variant="amber"
                  onClick={() => onAction(user, "reset-password-email")}
                />
                <ActionItem
                  icon={<Key className="h-4.5 w-4.5" />}
                  label="Generar contraseña directa"
                  variant="amber"
                  onClick={() => onAction(user, "reset-password-direct")}
                />

                <div className="mx-5 my-1 border-t border-gray-100 dark:border-gray-700/50" />
                <SectionLabel>Cuenta</SectionLabel>
                <ActionItem
                  icon={<Mail className="h-4.5 w-4.5" />}
                  label="Enviar verificación por correo"
                  variant="green"
                  onClick={() => onAction(user, "verify-email")}
                />
                <ActionItem
                  icon={<ShieldCheck className="h-4.5 w-4.5" />}
                  label="Verificar directamente"
                  variant="green"
                  onClick={() => onAction(user, "verify-direct")}
                />
                {user.active ? (
                  <ActionItem
                    icon={<Shield className="h-4.5 w-4.5" />}
                    label="Deshabilitar cuenta"
                    variant="destructive"
                    onClick={() => onAction(user, "disable")}
                    disabled={isCurrentUser}
                  />
                ) : (
                  <ActionItem
                    icon={<Shield className="h-4.5 w-4.5" />}
                    label="Habilitar cuenta"
                    variant="blue"
                    onClick={() => onAction(user, "enable")}
                  />
                )}
              </>
            )}

            {canManageApiKeys && (
              <>
                <div className="mx-5 my-1 border-t border-gray-100 dark:border-gray-700/50" />
                <SectionLabel>Avanzado</SectionLabel>
                <ActionItem
                  icon={<KeyRound className="h-4.5 w-4.5" />}
                  label="Gestionar API Keys"
                  variant="purple"
                  onClick={() => onAction(user, "api-keys")}
                />
                <ActionItem
                  icon={<KeyRound className="h-4.5 w-4.5" />}
                  label="Gestionar Licencias"
                  variant="blue"
                  onClick={() => onAction(user, "licenses")}
                />
              </>
            )}
          </div>

          {/* Close button at bottom */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/30">
            <Button
              variant="outline"
              className="w-full h-9 text-xs font-bold rounded-xl"
              onClick={onClose}
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
